import Papa from 'papaparse';
import { useState } from 'react';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { CallFilters } from 'src/interfaces/call.interface';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { Modal } from './Modal';
import { Tooltip } from './Tooltip';

const allCsvOptions: { value: string; label: string }[] = [
  { value: 'id', label: 'ID' },
  { value: 'agent', label: 'Agent' },
  { value: 'agent_name', label: 'Agent Name' },
  { value: 'transcript', label: 'Transcript' },
  { value: 'start_time', label: 'Start Time' },
  { value: 'end_time', label: 'End Time' },
  { value: 'duration', label: 'Duration (s)' },
  { value: 'from_number', label: 'From Number' },
  { value: 'to_number', label: 'To Number' },
  { value: 'status', label: 'Status' },
  { value: 'post_call_result', label: 'Post Call Result' },
  { value: 'telephony_metadata_call_sid', label: 'Call SID' },
  { value: 'telephony_metadata_account_sid', label: 'Account SID' },
  { value: 'telephony_metadata_stream_sid', label: 'Stream SID' },
  { value: 'telephony_metadata_encoding', label: 'Encoding' },
  { value: 'telephony_metadata_provider', label: 'Provider' },
];

type CallsCsvExportProps = {
  filters: CallFilters;
};

export const CallsCsvExport = ({ filters }: CallsCsvExportProps) => {
  const { get } = useAuthedRequest();
  const notification = useNotification();
  const [loadingCsv, setLoadingCsv] = useState(false);
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
  const [csvColumns, setCsvColumns] = useState<string[]>(() =>
    allCsvOptions.map((o) => o.value),
  );
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  const onDragStart = (index: number) => setDraggingIndex(index);
  const onDragOver = (e: React.DragEvent<HTMLElement>) => e.preventDefault();
  const onDrop = (index: number) => {
    setCsvColumns((prev) => {
      if (draggingIndex === null || draggingIndex === index) return prev;
      const next = [...prev];
      const [moved] = next.splice(draggingIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDraggingIndex(null);
  };

  const requestReportCSV = async () => {
    setLoadingCsv(true);

    const formattedFilters: CallFilters = {
      ...filters,
      ...(filters.agent === 'All Agents' && { agent: undefined }),
      ...((filters.status === 'All Statuses' ||
        filters.status === 'voicemail' ||
        filters.status === 'is_not_voicemail') && { status: undefined }),
      ...(filters.status === 'voicemail' && { is_voicemail: true }),
      ...(filters.status === 'is_not_voicemail' && { is_voicemail: false }),
      ...(filters.successful_actions?.length === 0 && {
        successful_actions: undefined,
      }),
    };

    const queryObject = {
      filters: JSON.stringify(formattedFilters),
    } as Record<string, string>;

    const query = new URLSearchParams(queryObject);

    try {
      const response = await get(`/calls/report?${query.toString()}`);
      if (response.length === 0) return notification.error('No data found');

      const formattedCallRecords: Record<string, unknown>[] = response.map(
        (row: Record<string, unknown>) => {
          const startSeconds = Number(row.start_time);
          const endSeconds = Number(row.end_time);
          const startDate = isNaN(startSeconds)
            ? null
            : new Date(startSeconds * 1000);
          const endDate = isNaN(endSeconds)
            ? null
            : new Date(endSeconds * 1000);

          const durationSeconds =
            startDate && endDate
              ? Math.round((endDate.getTime() - startDate.getTime()) / 1000)
              : '';

          const postCallResults = (row as Record<string, unknown>)[
            'post_call_results'
          ] as { content?: string } | undefined;
          const postCallResult = postCallResults?.content ?? '';

          return {
            ...row,
            start_time: startDate ? startDate.toISOString() : '',
            end_time: endDate ? endDate.toISOString() : '',
            duration: durationSeconds,
            post_call_result: postCallResult,
          } as Record<string, unknown>;
        },
      );

      const availableHeaders = Object.keys(formattedCallRecords[0]);
      const headersToUse =
        csvColumns.length > 0 ? csvColumns : availableHeaders;

      const formattedCallRecordsWithHeaders = formattedCallRecords.map(
        (row) => {
          return Object.fromEntries(
            headersToUse.map((header) => [header, row[header]]),
          );
        },
      );

      const blob = new Blob([Papa.unparse(formattedCallRecordsWithHeaders)], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', 'calls.csv');
      link.click();

      URL.revokeObjectURL(url);
      return notification.success('CSV was downloaded successfully');
    } catch (error) {
      console.error('Error requesting CSV:', error);
      notification.error('Failed to request CSV');
    } finally {
      setLoadingCsv(false);
    }
  };

  return (
    <>
      <Tooltip content="Export to CSV" position="bottom">
        <Button
          onClick={() => setIsCsvModalOpen(true)}
          color="primary"
          disabled={loadingCsv}
        >
          {loadingCsv ? 'Exporting CSV...' : 'Export CSV'}
        </Button>
      </Tooltip>

      <Modal
        isOpen={isCsvModalOpen}
        onClose={() => setIsCsvModalOpen(false)}
        title="Select CSV Columns"
        actionButton={
          <Button
            disabled={csvColumns.length === 0}
            onClick={() => {
              if (csvColumns.length === 0) return;
              setIsCsvModalOpen(false);
              requestReportCSV();
            }}
          >
            Export
          </Button>
        }
        className="lg:max-w-[700px]"
      >
        <div className="mb-4">
          <label className="flex items-center gap-2">
            <Checkbox
              ariaLabel="Select all columns"
              checked={csvColumns.length === allCsvOptions.length}
              indeterminate={
                csvColumns.length > 0 &&
                csvColumns.length < allCsvOptions.length
              }
              onChange={(isChecked) => {
                setCsvColumns(
                  isChecked ? allCsvOptions.map((o) => o.value) : [],
                );
              }}
            />
            <span className="text-sm font-medium">Select all</span>
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {allCsvOptions.map((opt) => {
            const checked = csvColumns.includes(opt.value);
            return (
              <label key={opt.value} className="flex items-center gap-2">
                <Checkbox
                  ariaLabel={opt.label}
                  checked={checked}
                  onChange={(isChecked) => {
                    setCsvColumns((prev) => {
                      if (isChecked)
                        return Array.from(new Set([...prev, opt.value]));
                      return prev.filter((v) => v !== opt.value);
                    });
                  }}
                />
                <span className="text-sm">{opt.label}</span>
              </label>
            );
          })}
        </div>

        <div className="mt-6">
          <p className="text-sm font-medium mb-2">
            Header order (drag to reorder)
          </p>
          {csvColumns.length === 0 ? (
            <i className="text-sm text-neutral-500">No columns selected</i>
          ) : (
            <div className="overflow-auto">
              <table className="min-w-full border border-neutral-200 rounded">
                <thead>
                  <tr>
                    {csvColumns.map((value, index) => {
                      const label =
                        allCsvOptions.find((o) => o.value === value)?.label ||
                        value;
                      const isDragging = index === draggingIndex;
                      return (
                        <th
                          key={`${value}-${index}`}
                          draggable
                          onDragStart={() => onDragStart(index)}
                          onDragOver={onDragOver}
                          onDrop={() => onDrop(index)}
                          className={`text-left bg-neutral-50 border-b border-neutral-200 px-3 py-2 cursor-move whitespace-nowrap ${
                            isDragging ? 'opacity-60' : ''
                          }`}
                        >
                          {label}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
              </table>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default CallsCsvExport;
